import React from "react";
import PropTypes from "prop-types";
import "./FilterSelect.scss";

/**
 * FilterSelect — a labelled dropdown filter for tool toolbars.
 *
 * Tools were each hand-rolling `<label>` + a bare `<select>`, which gave
 * inconsistent label sizing, no affordance that the control was a dropdown, and
 * cramped spacing. This is the shared equivalent: label and control read as one
 * unit, with a chevron so it's visibly a dropdown.
 *
 * Distinct from `Form/Select`, which is a full-width form field with its own
 * label block — this is sized and spaced for a horizontal toolbar.
 *
 * Options may carry a `count`, rendered as "Label (12)" — common in filters
 * where knowing how much each option hides is part of the decision.
 */
export const FilterSelect = ({
  label,
  value,
  onChange,
  options = [],
  title,
  disabled = false,
  className = "",
}) => (
  <label className={`filter-select-field ${className}`} title={title}>
    {label && <span className="filter-select-field__label">{label}</span>}
    <span className="filter-select-field__control">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.count == null ? opt.label : `${opt.label} (${opt.count})`}
          </option>
        ))}
      </select>
      <svg
        className="filter-select-field__chevron"
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        <path
          d="M2.5 4.5L6 8l3.5-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </label>
);

FilterSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
    })
  ).isRequired,
  title: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default FilterSelect;
